import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { getCatalog } from '../services/wholesaleApi';
import config from '../config/appConfig';
import '../styles/Wholesale.css';

const TYPE_ICONS = {
  card:'💳', bracelet:'⌚', keychain:'🔑', sticker:'🏷',
  tag:'🏷', pendant:'📿', badge:'🎖', other:'📦'
};

// ── Auth gate — shown when not logged in as provider ──────────
function WholesaleGate() {
  return (
    <div className="ws-page">
      <div className="ws-hero" style={{ minHeight: '100vh', display: 'flex', alignItems: 'center' }}>
        <div className="ws-hero-inner" style={{ maxWidth: 520 }}>
          <h1>{config.appIcon} {config.appName}</h1>
          <p className="ws-hero-sub">Wholesale Portal</p>
          <p className="ws-hero-desc">
            Access our full product catalog with volume pricing, place wholesale orders,
            and manage your account — exclusively for verified veterinary clinics,
            shelters, and pet service providers.
          </p>

          <div style={{
            background: 'rgba(255,255,255,.12)', backdropFilter: 'blur(10px)',
            borderRadius: 14, padding: '2rem', marginTop: '1.5rem',
            border: '1px solid rgba(255,255,255,.2)'
          }}>
            <h2 style={{ fontSize: '1.1rem', marginBottom: '1rem', color: '#fff' }}>
              Sign in to view the catalog
            </h2>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '.75rem' }}>
              <Link to="/login/provider" className="ws-btn ws-btn-light" style={{ justifyContent: 'center', width: '100%' }}>
                Provider Login
              </Link>
              <Link to="/login/agent" className="ws-btn ws-btn-outline" style={{ justifyContent: 'center', width: '100%' }}>
                Agent Login
              </Link>

              <div style={{
                borderTop: '1px solid rgba(255,255,255,.2)',
                marginTop: '.5rem', paddingTop: '1rem',
                textAlign: 'center'
              }}>
                <p style={{ fontSize: '.85rem', color: 'rgba(255,255,255,.7)', marginBottom: '.5rem' }}>
                  Not a partner yet?
                </p>
                <Link to="/register/provider" className="ws-btn ws-btn-outline" style={{ justifyContent: 'center', width: '100%' }}>
                  Apply for a Wholesale Account
                </Link>
              </div>
            </div>
          </div>

          <p style={{ fontSize: '.8rem', color: 'rgba(255,255,255,.5)', marginTop: '1.5rem' }}>
            Individual pet owners can register at <Link to="/register/user" style={{ color: 'rgba(255,255,255,.7)' }}>phrid.com/register</Link>
          </p>
        </div>
      </div>
    </div>
  );
}

export default function WholesaleCatalog() {
  const { user, loading: authLoading } = useAuth();
  const [products, setProducts] = useState([]);
  const [loading, setLoading]   = useState(true);
  const [filter, setFilter]     = useState('');
  const [search, setSearch]     = useState('');

  const isProvider = user && (user.role === 'provider' || user.role === 'agent');

  useEffect(() => {
    if (!isProvider) return;
    getCatalog({ itemType: filter || undefined, search: search || undefined })
      .then(r => setProducts(r.data.products))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [filter, search, isProvider]);

  // Show gate if not logged in as provider
  if (authLoading) return <div className="ws-page"><div className="ws-loading">Loading...</div></div>;
  if (!isProvider) return <WholesaleGate />;

  const formatPrice = (p) => `$${p.toFixed(2)}`;

  return (
    <div className="ws-page">
      {/* Hero */}
      <div className="ws-hero">
        <div className="ws-hero-inner">
          <h1>{config.appIcon} {config.appName}</h1>
          <p className="ws-hero-sub">Wholesale Product Catalog</p>
          <p className="ws-hero-desc">
            QR-enabled and NFC-ready products for veterinary clinics, shelters, and pet service providers.
            Volume pricing available.
          </p>
          <div className="ws-hero-actions">
            <Link to="/register/provider" className="ws-btn ws-btn-light">
              Register as Provider
            </Link>
            <Link to="/login/provider" className="ws-btn ws-btn-outline">
              Provider Login
            </Link>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="ws-container">
        <div className="ws-toolbar">
          <div className="ws-search">
            <input
              type="text"
              placeholder="Search products..."
              value={search}
              onChange={e => setSearch(e.target.value)}
            />
          </div>
          <div className="ws-filters">
            {['', 'tag', 'card', 'keychain', 'bracelet', 'pendant', 'badge', 'sticker'].map(t => (
              <button
                key={t}
                className={`ws-filter-btn ${filter === t ? 'active' : ''}`}
                onClick={() => setFilter(t)}
              >
                {t ? `${TYPE_ICONS[t] || ''} ${t.charAt(0).toUpperCase() + t.slice(1)}` : 'All'}
              </button>
            ))}
          </div>
        </div>

        {/* Product grid */}
        {loading ? (
          <div className="ws-loading">Loading catalog...</div>
        ) : products.length === 0 ? (
          <div className="ws-empty">
            <div className="ws-empty-icon">📦</div>
            <p>No products available{filter ? ` for "${filter}"` : ''}. Check back soon.</p>
          </div>
        ) : (
          <div className="ws-grid">
            {products.map(p => (
              <div key={p._id} className="ws-card">
                <div className="ws-card-img">
                  {p.images?.[0]?.url ? (
                    <img src={p.images[0].url} alt={p.name} />
                  ) : (
                    <div className="ws-card-placeholder">
                      {TYPE_ICONS[p.itemType] || '📦'}
                    </div>
                  )}
                  {p.nfcEnabled && <span className="ws-badge-nfc">NFC</span>}
                  {!p.inStock && <span className="ws-badge-oos">Out of Stock</span>}
                </div>

                <div className="ws-card-body">
                  <div className="ws-card-type">{p.itemType}</div>
                  <h3>{p.name}</h3>
                  <p className="ws-card-desc">{p.shortDescription}</p>

                  {p.features?.length > 0 && (
                    <ul className="ws-features">
                      {p.features.slice(0, 3).map((f, i) => <li key={i}>{f}</li>)}
                    </ul>
                  )}

                  <div className="ws-card-pricing">
                    <span className="ws-price">From {formatPrice(
                      p.priceTiers?.length
                        ? Math.min(...p.priceTiers.map(t => t.unitPrice))
                        : p.basePrice
                    )}/ea</span>
                    {p.minOrderQuantity > 1 && (
                      <span className="ws-moq">Min. {p.minOrderQuantity} units</span>
                    )}
                  </div>

                  {p.priceTiers?.length > 0 && (
                    <div className="ws-tiers">
                      <table>
                        <thead><tr><th>Qty</th><th>Price/ea</th></tr></thead>
                        <tbody>
                          {p.priceTiers.map((t, i) => (
                            <tr key={i}>
                              <td>{t.minQuantity}{t.maxQuantity ? `–${t.maxQuantity}` : '+'}</td>
                              <td>{formatPrice(t.unitPrice)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}

                  <div className="ws-card-meta">
                    {p.material && <span>📐 {p.material}</span>}
                    {p.leadTimeDays && <span>📅 {p.leadTimeDays} day lead</span>}
                    {p.includesQrCode && <span>▣ QR included</span>}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* CTA */}
        <div className="ws-cta">
          <h2>Ready to order?</h2>
          <p>Register as a verified service provider to access wholesale pricing and place orders directly.</p>
          <div className="ws-hero-actions">
            <Link to="/register/provider" className="ws-btn ws-btn-primary">Get Started</Link>
            <a href={`mailto:${config.supportEmail}`} className="ws-btn ws-btn-outline-dark">Contact Sales</a>
          </div>
        </div>
      </div>
    </div>
  );
}
